import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getDisposedAssets, restoreAsset } from '../../../models/assetsModel';
import { useHasAnyPermission } from '../../../utils/permissions';
import Button from '../../../components/common/Button';

function DisposedAssets() {
  const canViewAssets = useHasAnyPermission(['View Assets']);
  const canManageAssets = useHasAnyPermission(['Manage Assets']);
  
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchDisposedAssets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDisposedAssets();
      setAssets(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load disposed assets');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDisposedAssets();
  }, [fetchDisposedAssets]);

  const handleRestore = async (asset) => {
    if (window.confirm(`Are you sure you want to restore "${asset.asset_name}"?`)) {
      try {
        await restoreAsset(asset.asset_id);
        toast.success('Asset restored successfully');
        fetchDisposedAssets();
      } catch (error) {
        toast.error('Failed to restore asset');
      }
    }
  };

  const filteredAssets = assets.filter(asset =>
    asset.asset_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.asset_tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check permissions after all hooks are defined
  if (!canViewAssets) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Access Denied</div>
          <div className="text-gray-600">You do not have permission to view assets.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
        {/* Professional Header Card - Following COMPONENT_DESIGN_PATTERNS.md */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Disposed Assets</h1>
                <p className="text-xs text-gray-500 mb-1">Manage and restore disposed assets from the asset bin</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {assets.length > 0 ? `${assets.length} disposed assets` : 'No disposed assets'}
                    {searchTerm && (
                      <span className="ml-1 text-blue-600">
                        ({filteredAssets.length} filtered)
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Summary Stats and Controls */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: Summary Statistics */}
              {!loading && assets.length > 0 && (
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-600">{assets.length}</div>
                    <div className="text-xs text-gray-600">Total Disposed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">
                      R {assets.reduce((sum, asset) => sum + (parseFloat(asset.asset_cost) || 0), 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-600">Value Lost</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">
                      {filteredAssets.length}
                    </div>
                    <div className="text-xs text-gray-600">Showing</div>
                  </div>
                </div>
              )}
              
              {/* Row 2: Controls and Search Inline */}
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button 
                  onClick={fetchDisposedAssets} 
                  className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                  title="Refresh"
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0087D4' : 'currentColor'} style={{ color: loading ? '#0087D4' : 'currentColor' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                {/* Search Box */}
                <div className="relative w-64">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search disposed assets..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Disposed Assets Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Asset Details
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Category
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Disposal Info
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Value
                  </th>
                  {canManageAssets && (
                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={canManageAssets ? "5" : "4"} className="px-3 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredAssets.length === 0 ? (
                  <tr>
                    <td colSpan={canManageAssets ? "5" : "4"} className="px-3 py-8 text-center">
                      <div className="flex flex-col items-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <div className="mt-2 text-sm text-gray-500">
                          {searchTerm ? 'No matching disposed assets found' : 'No disposed assets found'}
                        </div>
                        {searchTerm && (
                          <p className="mt-1 text-xs text-gray-400">
                            Try adjusting your search terms
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAssets.map((asset, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {asset.asset_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Tag: {asset.asset_tag}
                          </div>
                          {asset.asset_serial && (
                            <div className="text-xs text-gray-400">
                              Serial: {asset.asset_serial}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{asset.category}</div>
                        <div className="text-sm text-gray-500">{asset.vendor}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div>
                          {asset.asset_deleted && (
                            <div className="text-sm text-gray-900">
                              Disposed: {asset.asset_deleted}
                            </div>
                          )}
                          {asset.asset_disposed_details && (
                            <div className="text-sm text-gray-500">
                              {asset.asset_disposed_details}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {asset.asset_cost ? `R ${parseFloat(asset.asset_cost).toLocaleString()}` : 'N/A'}
                      </td>
                      {canManageAssets && (
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestore(asset)}
                            className="text-green-600 hover:text-green-800"
                          >
                            Restore
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer - Following COMPONENT_DESIGN_PATTERNS.md */}
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              {/* Results info */}
              <div className="text-gray-500">
                {filteredAssets.length === 0 ? (
                  'No results found'
                ) : (
                  `Showing ${filteredAssets.length} of ${assets.length} disposed assets`
                )}
              </div>
              
              {searchTerm && (
                <div className="text-blue-600">
                  Filtered by: "{searchTerm}"
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Action info */}
              <div className="text-gray-500">
                {canManageAssets ? 'Click "Restore" to recover assets' : 'View only'}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}

export default DisposedAssets;
